import React, { useContext, useEffect, useRef, useState } from 'react'
import { logDevError } from '@agtech/core/logging/AgTechLogger';
import { useAgTechSafeState } from '@agtech/core/utilities/AgTechUtilities';
import { FormContext } from '@agtech/core/forms/AgTechFormContext';
import { AgTechFormControlMetadata, AgTechFormControlProps } from '@agtech/core/forms/AgTechFormControls';
import { AgTechDatePickerChangeHandler } from './Date/AgTechDatePicker';
import { useAgTechDatePickerPopUp } from './Date/AgTechDatePickerPopUp';

export type AgTechFormDateControlProps<TEntity> = AgTechFormControlProps<TEntity, string> & {
    allowPreviousDates?: boolean
}

export const AgTechFormDateControl = <TFormEntity, >(props: AgTechFormDateControlProps<TFormEntity>) => {
    const formContext = useContext(FormContext);
    const inputRef = useRef<HTMLInputElement>(null);
    const dateChangeRef = useRef<AgTechDatePickerChangeHandler>({
        onDateChanged: () => { 

        }
    });

    let [dateValue, updateDateValue] = useAgTechSafeState<string>(props.value(formContext.entity));

    let [field, updateField] = useState<AgTechFormControlMetadata<TFormEntity>>({
        id: props.caption,
        isValid: true,
        validateOnSubmit: (entity: any) => validateControlOnFormSubmit(entity),
    });

    let markFieldValid = () => updateField({...field, isValid: true });
    let markFieldInvalid = () => updateField({...field, isValid: false });

    let validateControlOnFormSubmit = (entity: any): string => {
        let fieldValidationWarningMessage = '';
        let controlValue = props.value(entity);

        if (props.validation?.validateOnSubmit) {
            fieldValidationWarningMessage = props.validation.validateOnSubmit(formContext.entity, controlValue) ?? '';
        }

        if (fieldValidationWarningMessage) {
            markFieldInvalid();
        }

        return fieldValidationWarningMessage;
    }

    let formControlWidth: string = `${props.width ?? 50}%`;
    let formControlStatus: string = field.isValid ? '' : 'invalid';

    let handleDateChanged = (updatedDate: string) => {
        updateDateValue(updatedDate);

        dateChangeRef.current?.onDateChanged(updatedDate);

        if (props.onControlValueChanged) {
            formContext.handleFieldChanged(updatedDate, props.onControlValueChanged);

            if (props.validation?.validateOnFocusOut && !props.validation.validateOnFocusOut(updatedDate)) {
                markFieldInvalid();
            }
            else if (!field.isValid) {
                markFieldValid();
            }
        }
    }

    useAgTechDatePickerPopUp({
        inputRef: inputRef,
        dateValue: dateValue,
        dateChangedHandlerRef: dateChangeRef,
        initialDateValue: dateValue.current,
        onDateSelected: handleDateChanged
    });

    useEffect(() => {
        formContext.registerFormField(field);

        if (inputRef.current && props.focused) {
            inputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        handleDateChanged(props.value(formContext.entity))
    },
    [props.value(formContext.entity)]);

    return (
        <div className={`agtech-form-control ${formControlStatus}`} style={{maxWidth: formControlWidth}}>
            <h1 className="agtech-form-control-caption">{props.caption + (props.required ? ' *' : '')}</h1>
            <input
                type="text"
                value={dateValue.current}
                onChange={e => handleDateChanged(e.target.value)}
                readOnly={props.readonly}
                ref={inputRef}
                placeholder={props.placeHolder ?? ''}
            />
        </div>
    );
}